import { vatIncludedBreakdown, type PaymentsConfig } from '@tenders/shared';
import { countryConfig } from '../country';
import { formatBudget, withCurrency } from './format';

type Translate = (key: string, params?: Record<string, string | number>) => string;

/**
 * The words after a plan amount that say where the tax stands:
 *
 * - `payments.vatNotCharged` (Spain since 2026-10-20 — no IVA is charged at
 *   all): «· IVA no incluido»;
 * - `payments.vatIncludedRate` (Chile — the price includes IVA 19 %):
 *   «· IVA incluido»;
 * - `payments.stripeTax` (no deployment today): «+ IVA» when prices are quoted
 *   before tax, «(IVA incluido)» when they are final.
 *
 * Null on every deployment with neither (UZ, KZ), whose pages therefore render
 * exactly as before.
 *
 * Every place that prints what a plan costs appends it — the plan card, its pay
 * button, the upgrade teaser, the «next charge» line — so no amount on the page
 * can be read as including a tax it does not (TRLGDCU art. 60.2.c, 97.1.e).
 */
export function vatNote(
  t: Translate,
  payments: Pick<
    PaymentsConfig,
    'stripeTax' | 'vatNotCharged' | 'vatIncludedRate'
  > = countryConfig.payments,
): string | null {
  if (payments.vatNotCharged) return t('plans.vat.notCharged');
  if (payments.vatIncludedRate) return t('plans.vat.includedCl');
  if (payments.stripeTax?.taxBehavior === 'exclusive') return t('plans.vat.excluded');
  if (payments.stripeTax?.taxBehavior === 'inclusive') return t('plans.vat.included');
  return null;
}

/**
 * Chile (`payments.vatIncludedRate`): how much of a price is IVA, in the place
 * where Spain explains why it charges none — «Incluye $ 6.385 de IVA (19 %)» for
 * $ 39.990 (`vatIncludedBreakdown`: net + IVA always add up to the price).
 * Display only. Null on every deployment without the rate (UZ, KZ, ES) and for
 * a free plan.
 */
export function vatIncludedLine(
  t: Translate,
  gross: number,
  payments: Pick<PaymentsConfig, 'vatIncludedRate'> = countryConfig.payments,
  symbol: string = countryConfig.currency.symbol,
): string | null {
  const rate = payments.vatIncludedRate;
  if (!rate || !(gross > 0)) return null;
  const { vat } = vatIncludedBreakdown(gross, rate);
  const pct = String(Math.round(rate * 1000) / 10).replace('.', ',');
  return t('plans.vat.includedBreakdown', {
    vat: withCurrency(formatBudget(String(vat)), symbol),
    rate: pct,
  });
}
